import { useEffect, useMemo, useState } from "react";
import {
  CaseEvent,
  CommitteeType,
  CommitteeTypeLabelMap,
  EventEntityType,
  EventType,
  EventTypeLabelMap,
  LEAType,
  LEATypeLabelMap,
} from "../../../api/events";
import { useAppDispatch, useAppSelector } from "../../../../src/app/store";
import { ChevronDown, ChevronUp } from "lucide-react";
import DocumentTooltip from "../../documentManagement/components/DocumentTooltip";
import { formatDateStringForDisplay } from "../../../lib/utils";
import { getUsersByEmailIds } from "../../users/logic/slice";
import { fetchUsersWithEmailIds } from "../../users/logic/thunk";
import { getPartyPerpetratorsByIds } from "../../stakeholder/logic/stakeholderSlice";
import { getAccountProductInfo } from "../../creditAccounts/logic/slice";

export interface TimelineStageProps {
  eventType: EventType;
  events: CaseEvent[];
  notes?: string; //Optional for now. We can remove it later based on useCase
}

export const TimelineStage: React.FC<TimelineStageProps> = ({
  eventType,
  events,
  notes,
}) => {
  const entityType = events[0]?.entity_type;
  const isCaseLevel = entityType === EventEntityType.CASE;
  const isEntityLevel = !isCaseLevel;

  const headerDate = useMemo(() => {
    return events.reduce((latest, event) => {
      return new Date(event.date) > new Date(latest)
        ? event.date
        : latest;
    }, events[0]?.date);
  }, [events]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchUsersWithEmailIds(events.map((event => event.actor_email))));
  }, [events])

  const stageLabel = EventTypeLabelMap[eventType];

  const isExpandable = isEntityLevel || (isCaseLevel && Boolean(notes));

  const userMap = useAppSelector((state) => getUsersByEmailIds(state, events.map((event => event.actor_email))));

  const [isExpanded, setIsExpanded] = useState(false);

  const partiesById = useAppSelector(
    getPartyPerpetratorsByIds(
      events.filter((event) => event.entity_type === EventEntityType.PARTY_INVOLVED).map((event)=>event.entity_id)
    )
  );
  const accountProductInfo = useAppSelector(getAccountProductInfo);
  const extAccountNumberById = useMemo(
    () =>
      Object.fromEntries(
        accountProductInfo.map((account) => [
          account.loan_reference_id,
          account.ext_account_number,
        ]),
      ),
    [accountProductInfo],
  );

  const getEntityDisplayName = (event: CaseEvent) => {
    switch (event.entity_type) {
      case EventEntityType.PARTY_INVOLVED: {
        return partiesById[event.entity_id]?.name ?? event.entity_id;
      }

      case EventEntityType.LEA:
        return (
          LEATypeLabelMap[event.entity_id as LEAType] ??
          event.entity_id
        );

      case EventEntityType.COMMITTEE:
        return (
          CommitteeTypeLabelMap[event.entity_id as CommitteeType] ??
          event.entity_id
        );

      case EventEntityType.LOAN_ACCOUNT:
        return extAccountNumberById[event.entity_id];

      default:
        return event.entity_id;
    }
  };

  const shouldHideEntityIdForEvent = (event: CaseEvent): boolean =>
    event.entity_type === EventEntityType.POST_IR_ASSESSMENT ||
    event.entity_type === EventEntityType.PRE_SCN_FIC_MEETING;

  const actorSummary = events
    .map((event) => {
      const email = event.actor_email;
      return userMap?.[email]?.user_name ?? email;
    })
    .filter((value, index, self) => self.indexOf(value) === index)
    .join(", ");
  
    const getEventDisplayText = (event: CaseEvent) => {
      const dateStr = event.date ? formatDateStringForDisplay(event.date) : "";

      if (shouldHideEntityIdForEvent(event)) return dateStr;

      return `${getEntityDisplayName(event)} – ${dateStr}`;
    };

  if (!events.length) return null;

  return (
    <div className="flex flex-col gap-1 mb-[20px]">
      <div className="grid grid-cols-[auto,1fr,auto] items-center gap-2">
        <span className="text-sm text-[#374151] whitespace-nowrap">
          {headerDate ? formatDateStringForDisplay(headerDate) : ""}
        </span>

        <DocumentTooltip content={stageLabel}>
          <span className="text-sm font-semibold text-[#0A0A0A] truncate">
            {stageLabel}
          </span>
        </DocumentTooltip>

        {isExpandable && (
          <button
            onClick={() => setIsExpanded((prev) => !prev)}
            className="text-gray-500 hover:text-gray-700"
          >
            {isExpanded ? (
              <ChevronUp size={16} strokeWidth={1.5} />
            ) : (
              <ChevronDown size={16} strokeWidth={1.5} />
            )}
          </button>
        )}
      </div>

      <div className="text-xs leading-4 font-normal text-[#737373]">
          {actorSummary}
      </div>

      {isExpanded && (
        <div className="flex flex-col gap-2 pt-1">
          {isEntityLevel && (
            <div className="flex flex-col gap-0 pl-[11px]">
              {events.map((event, index) => {
                const isLastItem = index === events.length - 1;

                return (
                  <div
                    key={event.event_id}
                    className="flex items-stretch relative"
                  >
                    <div className="relative w-[13px] shrink-0 mr-1">
                      {!isLastItem && (
                        <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-[#E5E5E5] z-0" />
                      )}

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="18"
                        viewBox="0 0 13 18"
                        fill="none"
                        className="absolute top-0 left-0 z-10 block"
                      >
                        <path
                          d="M0.5 0.5L0.5 9.5C0.5 13.9183 4.08172 17.5 8.5 17.5L12.5 17.5"
                          stroke="#E5E5E5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>

                    <div className="text-xs leading-[14px] text-gray-500 pt-[7px]">
                      {getEventDisplayText(event)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {notes && (
            <div className="w-full min-w-0">
              <div
                className="
                  flex flex-col justify-center
                  h-[57px] w-full
                  rounded-[8px]
                  bg-[#F5F5F5]
                  px-3 py-2
                "
              >
                <DocumentTooltip content={notes}>
                  <span
                    className="
                      text-sm leading-5 font-normal text-gray-700
                      line-clamp-2 break-words
                    "
                  >
                    {notes}
                  </span>
                </DocumentTooltip>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
